// What the landing (`/home`) and About Us (`/home/about`) SAY, as plain data:
// the crawler HTML the edge middleware puts in the page body, the structured
// data (SoftwareApplication, FAQPage, AboutPage), and llms.txt.
//
// Why it exists: the pages are client-rendered, and AI crawlers (GPTBot,
// ClaudeBot, PerplexityBot…) read raw HTML only — they never run JavaScript,
// so without this they see an empty `<div id="root">`. Everything here is read
// from the SAME i18n keys the React pages render, through a `CopyGetter`, so the
// crawler copy cannot drift from the page: the middleware passes raw locale
// JSON (`page-copy`), the client passes its i18n resources.
//
// Pure and edge-safe: no React, relative imports only.

import { APP_STORE_URL, PLAY_STORE_URL } from '../lib/store-links';
import { FALLBACK_USER_COUNT, formatCompactMillions } from '../lib/user-count-format';
import {
  buildBreadcrumbs,
  buildFaqPage,
  buildMobileApplications,
  buildSoftwareApplication,
  type FaqEntry,
} from '../metadata/json-ld';
import {
  ABOUT_VALUE_COPY,
  ABOUT_VALUE_KEYS,
  BIO_KEY,
  TEAM,
} from '../pages/landing-v3/about-team';
import {
  FEATURE_PAGES,
  featurePageForSeoKey,
  type Lang,
  localizePath,
  type MarketingRoute,
  SECTION_PAGES,
  type SectionPage,
} from './marketing-routes';
import { interpolate } from './seo-strings';

/** Reads one raw copy value (a string, or an array of paragraphs) by key. */
export type CopyGetter = (key: string) => unknown;

/** The landing FAQ's questions, in page order (`landingV3.faq.items.<key>`). */
export const LANDING_FAQ_KEYS = [
  'what',
  'llm',
  'memory',
  'materials',
  'anki',
  'subjects',
  'plans',
  'price',
  'progress',
  'privacy',
] as const;

/** The deck's ten features (`landingV3.features.<id>`), in the order the
 *  crawler copy and `featureList` name them: what students use most first —
 *  flashcards and quizzes are most of weekly use (user 2026-09-30). The page's
 *  own order (Create → Plan → Study) is the deck's, and deliberately not this
 *  one; page-content.spec pins that both walk the same ten. */
export const LANDING_FEATURE_IDS = [
  'flashcards',
  'quizzes',
  'spacedRepetition',
  'summaries',
  'mindMaps',
  'oralExam',
  'caseStudies',
  'examPlanner',
  'transcriber',
  'imageOcclusion',
] as const;

/** The four product tiles, in page order (`landingV3.tiles.<key>`). */
export const LANDING_TILE_KEYS = ['rank', 'timer', 'heat', 'grades'] as const;

// ── Reading copy ─────────────────────────────────────────────────────────────

const text = (get: CopyGetter, key: string): string => {
  const value = get(key);
  return typeof value === 'string' ? value : '';
};

/** i18next `<Trans>` markers (`<1>bold</1>`) carry no meaning outside React. */
const plain = (value: string): string => value.replace(/<\/?\d+>/g, '');

/** A value that may be one paragraph or several (an array). */
const paragraphs = (get: CopyGetter, key: string): string[] => {
  const value = get(key);
  return (Array.isArray(value) ? value : [value])
    .filter((p): p is string => typeof p === 'string' && p.trim() !== '')
    .map(plain);
};

/** Joins copy fragments the way the page lays them out. */
const words = (...parts: string[]): string =>
  parts
    .map((p) => p.trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+([.,!?:;])/g, '$1');

const esc = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// ── The landing ──────────────────────────────────────────────────────────────

export const landingHeadline = (get: CopyGetter): string =>
  words(
    text(get, 'landingV3.headline.pre'),
    text(get, 'landingV3.headline.ai'),
    text(get, 'landingV3.headline.post'),
    text(get, 'landingV3.headline.line2'),
  );

/** A tile title, stored in parts that already carry their spaces. */
export const landingTileTitle = (get: CopyGetter, key: string): string =>
  ['pre', 'hl', 'mid', 'hl2', 'post']
    .map((part) => text(get, `landingV3.tiles.${key}.${part}`))
    .join('');

export const landingFeatures = (get: CopyGetter) =>
  LANDING_FEATURE_IDS.map((id) => ({
    id,
    name: text(get, `landingV3.features.${id}.badge`),
    title: text(get, `landingV3.features.${id}.title`),
    description: text(get, `landingV3.features.${id}.subtitle`),
  }));

export type LandingFeatureId = (typeof LANDING_FEATURE_IDS)[number];

/** A feature's own URL (`FEATURE_PAGES`), in the page's language. */
const featureHref = (id: LandingFeatureId, lang: Lang): string | undefined => {
  const page = FEATURE_PAGES.find((f) => f.id === id);
  return page && localizePath(page.path, lang);
};

/**
 * A feature page's head when `seo.<seoKey>` has none yet: the feature card's
 * own words — what the visitor sees first on that URL. `<lang>-seo.json` wins
 * as soon as it has the key.
 */
const featurePageCopy = (
  get: CopyGetter,
  id: LandingFeatureId,
): { title: string; description: string } => {
  const name = text(get, `landingV3.features.${id}.badge`);
  const title = text(get, `landingV3.features.${id}.title`).replace(/[.!?]+$/, '');
  const subtitle = text(get, `landingV3.features.${id}.subtitle`);
  return {
    title: `${name} — ${title} | Flashka`,
    description: words(`${title}.`, subtitle),
  };
};

/** The agent chat (Ka edits your material on request), as the page says it. */
const agentLabel = (get: CopyGetter) => text(get, 'landingV3.footer.agent');
const agentHeadline = (get: CopyGetter) =>
  words(
    text(get, 'landingV3.agent.question'),
    text(get, 'landingV3.agent.pre') + text(get, 'landingV3.agent.hl'),
  );

/**
 * The head of one of the landing's own URLs (`LANDING_SUBPAGES`) when
 * `seo.<seoKey>` has none yet — the words the visitor sees first there — plus
 * its breadcrumb label. `<lang>-seo.json` wins as soon as it has the key.
 */
export const landingSubpageCopy = (
  get: CopyGetter,
  seoKey: string,
): { title: string; description: string; label: string; path: string } | undefined => {
  const feature = featurePageForSeoKey(seoKey);
  if (feature) {
    return {
      ...featurePageCopy(get, feature.id),
      label: text(get, `landingV3.features.${feature.id}.badge`),
      path: feature.path,
    };
  }
  const section = SECTION_PAGES.find((p) => p.seoKey === seoKey);
  if (section?.id === 'agent') {
    return {
      title: `${agentLabel(get)} — Professor Ka | Flashka`,
      description: text(get, 'landingV3.agent.summary'),
      label: agentLabel(get),
      path: section.path,
    };
  }
  return undefined;
};

/** Where one of the landing's own URLs opens: a feature card or a section. */
export type LandingFocus = LandingFeatureId | SectionPage['id'];

/** The FAQ as plain question / answer pairs (multi-paragraph answers joined). */
export const landingFaq = (get: CopyGetter): FaqEntry[] =>
  LANDING_FAQ_KEYS.map((key) => ({
    question: text(get, `landingV3.faq.items.${key}.q`),
    answer: paragraphs(get, `landingV3.faq.items.${key}.a`).join('\n\n'),
  })).filter((entry) => entry.question && entry.answer);

/** The registered-user floor the page shows before the live count arrives
 *  (rounded DOWN — never more than exist). */
const userFloor = (lang: Lang): string =>
  `${formatCompactMillions(FALLBACK_USER_COUNT, lang)}+`;

const footerLinks = (get: CopyGetter, lang: Lang): string =>
  [
    ['/home/about', text(get, 'landingV3.nav.aboutUs')],
    ['/blog', text(get, 'homeV2.footer.blog')],
    ['/help', text(get, 'homeV2.footer.helpTutorials')],
    ['/terms', text(get, 'homeV2.footer.termsConditions')],
    ['/privacy', text(get, 'homeV2.footer.privacyPolicy')],
  ]
    .filter(([, label]) => label)
    .map(
      ([path, label]) => `<a href="${esc(localizePath(path, lang))}">${esc(label)}</a>`,
    )
    .join(' · ');

const storeLinks = (get: CopyGetter): string =>
  `<a href="${esc(APP_STORE_URL)}">${esc(text(get, 'landingV3.stores.ios'))}</a> · <a href="${esc(PLAY_STORE_URL)}">${esc(text(get, 'landingV3.stores.android'))}</a>`;

/**
 * The landing's copy as semantic HTML, in page order (features most-used first,
 * see LANDING_FEATURE_IDS): the headline, the ten
 * feature cards, the agent chat, the four tiles, the section headlines, the FAQ with every
 * answer, and the footer's links. Only what the page shows.
 */
export const landingStaticHtml = (
  get: CopyGetter,
  lang: Lang,
  /** On a feature's or section's own URL: it leads, as the page's one h1. */
  focus?: LandingFocus,
): string => {
  const all = landingFeatures(get);
  const ordered = focus
    ? [...all.filter((f) => f.id === focus), ...all.filter((f) => f.id !== focus)]
    : all;
  const features = ordered
    .map((f) => {
      const h = f.id === focus ? 'h1' : 'h2';
      const href = featureHref(f.id, lang);
      const name = href ? `<a href="${esc(href)}">${esc(f.name)}</a>` : esc(f.name);
      return `<section><${h}>${esc(f.title)}</${h}><p><strong>${name}.</strong> ${esc(f.description)}</p></section>`;
    })
    .join('');
  const hero = focus ? 'h2' : 'h1';
  const agentPage = SECTION_PAGES.find((p) => p.id === 'agent');
  const agentH = focus === 'agent' ? 'h1' : 'h2';
  const agent = `<section><${agentH}>${esc(agentHeadline(get))}</${agentH}><p><strong>${
    agentPage
      ? `<a href="${esc(localizePath(agentPage.path, lang))}">${esc(agentLabel(get))}</a>`
      : esc(agentLabel(get))
  }.</strong> ${esc(text(get, 'landingV3.agent.summary'))}</p></section>`;
  const featureFirst = focus !== undefined && focus !== 'agent';
  const tiles = LANDING_TILE_KEYS.map(
    (key) => `<section><h2>${esc(landingTileTitle(get, key))}</h2></section>`,
  ).join('');
  const faq = landingFaq(get)
    .map(
      (e) =>
        `<h3>${esc(e.question)}</h3>${e.answer
          .split('\n\n')
          .map((p) => `<p>${esc(p)}</p>`)
          .join('')}`,
    )
    .join('');
  const trusted = words(
    text(get, 'landingV3.trusted.prefix'),
    userFloor(lang),
    text(get, 'landingV3.trusted.suffix'),
  );
  const social = words(
    interpolate(text(get, 'landingV3.social.count'), {
      total: formatCompactMillions(FALLBACK_USER_COUNT, lang),
    }),
    text(get, 'landingV3.social.students'),
  );
  const posts = words(text(get, 'landingV3.posts.hl'), text(get, 'landingV3.posts.post'));
  const final = words(
    text(get, 'landingV3.finalKa.pre'),
    text(get, 'landingV3.finalKa.hl'),
    text(get, 'landingV3.finalKa.post'),
  );
  const faqTitle =
    text(get, 'landingV3.faq.titlePre') + text(get, 'landingV3.faq.titleHl');
  return [
    `<header><nav><a href="${esc(localizePath('/home', lang))}">Flashka</a> · <a href="${esc(localizePath('/home/about', lang))}">${esc(text(get, 'landingV3.nav.aboutUs'))}</a></nav></header>`,
    '<main>',
    // The focused block first; otherwise page order (hero, deck, agent chat).
    ...(focus === 'agent' ? [agent] : featureFirst ? [features] : []),
    `<section><${hero}>${esc(landingHeadline(get))}</${hero}><p>${esc(trusted)}</p></section>`,
    ...(featureFirst ? [] : [features]),
    ...(focus === 'agent' ? [] : [agent]),
    tiles,
    `<p>${esc(text(get, 'landingV3.tiles.grades.footnote'))}</p>`,
    `<section><h2>${esc(social)}</h2></section>`,
    `<section><h2>${esc(posts)}</h2></section>`,
    `<section><h2>${esc(final)}</h2><p>${storeLinks(get)}</p></section>`,
    `<section><h2>${esc(faqTitle)}</h2><p>${esc(text(get, 'landingV3.faq.subtitle'))}</p>${faq}</section>`,
    '</main>',
    `<footer><nav>${footerLinks(get, lang)}</nav></footer>`,
  ].join('');
};

// ── About Us ─────────────────────────────────────────────────────────────────

/** About Us as semantic HTML: the hero, the whole story (the part behind "Read
 *  more" included), the six values, the team and the invitation to join. */
export const aboutStaticHtml = (get: CopyGetter, lang: Lang): string => {
  const values = ABOUT_VALUE_KEYS.map(
    (key) =>
      `<h3>${esc(text(get, ABOUT_VALUE_COPY[key].titleKey))}</h3><p>${esc(text(get, ABOUT_VALUE_COPY[key].descriptionKey))}</p>`,
  ).join('');
  const team = TEAM.map(
    (m) =>
      `<h3>${esc(m.name)}</h3><p>${esc(m.role)}</p><p>${esc(text(get, BIO_KEY[m.bio]))}</p>`,
  ).join('');
  const builtFor = words(
    text(get, 'landingV3.trusted.builtFor.prefix'),
    userFloor(lang),
    text(get, 'landingV3.trusted.builtFor.suffix'),
  );
  return [
    `<header><nav><a href="${esc(localizePath('/home', lang))}">Flashka</a></nav></header>`,
    '<main>',
    `<section><h1>${esc(words(text(get, 'homeV2.aboutPage.weveBeen'), text(get, 'homeV2.aboutPage.there')))}</h1><p>${esc(text(get, 'homeV2.aboutPage.buildingToolWeNeeded'))} ${esc(text(get, 'homeV2.aboutPage.createdByStudents'))}</p><p>${esc(builtFor)}</p></section>`,
    `<section><h2>${esc(text(get, 'homeV2.aboutPage.ourStory'))}</h2><p>${esc(text(get, 'homeV2.aboutPage.storyText'))}</p><p>${esc(text(get, 'homeV2.aboutPage.storyTextMore'))}</p></section>`,
    `<section><h2>${esc(words(text(get, 'homeV2.aboutPage.ourValues'), text(get, 'homeV2.aboutPage.values')))}</h2><p>${esc(text(get, 'homeV2.aboutPage.changingWorldEducation'))}</p>${values}</section>`,
    `<section><h2>${esc(words(text(get, 'homeV2.aboutPage.meetOurTeam'), text(get, 'homeV2.aboutPage.team')))}</h2><p>${esc(text(get, 'homeV2.aboutPage.committedToChange'))}</p>${team}</section>`,
    `<section><h2>${esc(text(get, 'homeV2.aboutPage.joinOurVision'))}</h2><p>${esc(text(get, 'homeV2.aboutPage.joinOurVisionDescription'))}</p></section>`,
    '</main>',
    `<footer><nav>${footerLinks(get, lang)}</nav></footer>`,
  ].join('');
};

// ── Structured data ──────────────────────────────────────────────────────────

/**
 * The page's own JSON-LD graphs (Organization + WebSite are site-wide and
 * added separately). Shared by the edge head and the client `<Seo>`, so a
 * crawler and a JS-rendering one read the same graphs.
 *
 * - `/home`: SoftwareApplication (with the ten features and the free plan), the
 *   two store apps (MobileApplication), and FAQPage (every answer in full).
 * - `/home/about`: AboutPage, and the Organization's people.
 * - Feature pages that declare `software`: a plain SoftwareApplication.
 *
 * No AggregateRating: our App Store / Google Play ratings are collected on
 * those stores, and Google's review-snippet rules forbid marking up ratings
 * aggregated from other sites on your own page.
 */
export const pageJsonLd = ({
  seoKey,
  route,
  lang,
  get,
  origin,
  canonical,
  title,
  description,
}: {
  seoKey: string;
  route?: MarketingRoute;
  lang: Lang;
  get: CopyGetter;
  origin: string;
  canonical: string;
  title: string;
  description: string;
}): object[] => {
  if (seoKey === 'home') {
    return [
      buildSoftwareApplication(origin, {
        description,
        inLanguage: lang,
        featureList: landingFeatures(get).map((f) => `${f.name}: ${f.description}`),
      }),
      ...buildMobileApplications(origin, { description, inLanguage: lang }),
      { ...buildFaqPage(landingFaq(get)), '@id': `${canonical}#faq`, inLanguage: lang },
    ];
  }
  if (seoKey === 'about') {
    const person = (m: (typeof TEAM)[number]) => ({
      '@type': 'Person',
      name: m.name,
      jobTitle: m.role,
    });
    const people = TEAM.filter((m) => m.photo);
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: title,
        description,
        inLanguage: lang,
        isPartOf: { '@id': `${origin}/#website` },
        about: { '@id': `${origin}/#organization` },
        mainEntity: { '@id': `${origin}/#organization` },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${origin}/#organization`,
        name: 'Flashka',
        founder: people.filter((m) => /co-founder/i.test(m.role)).map(person),
        employee: people.map(person),
      },
    ];
  }
  const subpage = landingSubpageCopy(get, seoKey);
  if (subpage) {
    // Flashka › the feature: the trail search shows under the result.
    return [
      buildSoftwareApplication(origin, { description, inLanguage: lang }),
      buildBreadcrumbs(origin, [
        { label: 'Flashka', href: localizePath('/home', lang) },
        { label: subpage.label, href: localizePath(subpage.path, lang) },
      ]),
    ];
  }
  if (route?.jsonLd?.includes('software')) {
    return [buildSoftwareApplication(origin, { description, inLanguage: lang })];
  }
  return [];
};
